import { ReactNode, useEffect, useMemo, useState } from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    DynamicValidationRule,
    ModelVariableScope,
    SelectionShape,
    SumRule,
} from "@/models";
import { useFormatVariableValue } from "@/hooks";
import {
    useIsSubmitting,
    usePromptsByForm,
} from "@/Pages/Participant/SimulationDisplay/redux-state/hooks";
import { useGetAllElementsAsync } from "@/hooks/useGetElementAsync";
import { createPortal } from "react-dom";
import { getBlockingErrorPromptIds } from "@/Pages/Participant/SimulationDisplay/state/blockingErrors";
import { isPromptUnanswered } from "@/Pages/Participant/SimulationDisplay/state/isPromptUnanswered";

const numericalInputTypes = ["NUMERICAL_INPUT", "NUMERICAL_SLIDER"];

/**
 * FormContentBlockDefaultValue is the initial data
 * for a given prompt based on prompt type (and thus form field type)
 * and any selections, if they exist.
 *
 * string:
 * MULTIPLE_CHOICE, DROPDOWN_LIST, TOGGLE_SWITCH, SHORT_TEXT, LONG_TEXT
 *
 * number:
 * NUMERICAL_INPUT, NUMERICAL_SLIDER
 *
 * string[]:
 * MULTIPLE_SELECT
 *
 * SelectionShape[]:
 * RANK_ORDER, DRAG_AND_DROP
 */
export type FormContentBlockDefaultValue =
    | string
    | number
    | string[]
    | SelectionShape[]
    | null;

export type FormContentBlockDefaultValuesObject = {
    [promptId: string]: FormContentBlockDefaultValue;
};

// export type FormContentBlockFieldPropsObject = {
//     [promptId: string]: QuestionContentBlockFormFieldDisplayProps;
// };

export type FormValidationSchema = z.ZodObject<
    any,
    "passthrough",
    z.ZodTypeAny,
    {
        [x: string]: any;
    },
    {
        [x: string]: any;
    }
>;

function findVariableValue(validationRule: SumRule) {
    const { variableValues } = validationRule;
    const possibleVariableValues = variableValues.filter((value) => {
        return (
            validationRule.model_variable_time_horizon_id === null ||
            validationRule.model_variable_time_horizon_id ===
                value.time_horizon_id
        );
    });

    return (
        possibleVariableValues.find((value) => {
            if (value.scope === ModelVariableScope.Team) return !!value.team_id;
            if (value.scope === ModelVariableScope.User) return !!value.user_id;
        }) || possibleVariableValues[0]
    );
}

interface Props {
    defaultValues: FormContentBlockDefaultValuesObject;
    onSubmit?: (
        formObject: FormContentBlockDefaultValuesObject,
    ) => Promise<void>;
    children?: ReactNode;
    validationRules: DynamicValidationRule[];
    isSubmitting: boolean;
    contentBlockId: string;
    // Imported designs render their own validation/remaining display (e.g. a
    // MODEL_VALUE_DISPLAY in the source layout), so the provider's built-in
    // message would be a duplicate. Non-imported (creator-built) forms — sliders
    // especially — still rely on the provider to show the message.
    isImported?: boolean;
}

// export const ConnectForm = ({ children }) => {
//     const methods = useFormContext();

//     return children({ ...methods });
// };

function valuesToFlatArray(values: {
    [index: string]: string | number | string[] | number[];
}) {
    return Object.keys(values).reduce((carry, key) => {
        if (Array.isArray(values[key])) {
            return [...carry, ...(values[key] as string[])];
        }
        return [...carry, values[key]];
    }, []);
}


export const DesignLayerFormContentBlockProvider = (props: Props) => {
    const { defaultValues, children, validationRules } = props;
    const formatVariableValue = useFormatVariableValue();

    const { setInvalid, setValid } = useIsSubmitting();
    type FormObject = typeof defaultValues;

    //build first and THEN refine, so that the value passed to refine is the whole form state, one hopes
    const validationSchema = useMemo(() => {
        const validation = z
            .object(
                validationRules.reduce((carry, rule) => {
                    const parentOptions = rule.options.filter(
                        (option) => option.relation_type === "parent",
                    );

                    if (parentOptions.length) {
                        return {
                            ...carry,
                            [parentOptions[0].prompt_id]: z.any(),
                        };
                    }
                    return carry;
                }, {}),
            )
            .partial()
            .superRefine((value, ctx) => {
                //require answer for all radio group

                const flatValues = valuesToFlatArray(value);

                //do dendency rules
                validationRules
                    .filter((rule) => rule.rule_type === "dependency")
                    .forEach((rule) => {
                        const unselectedParentOptions = rule.options.filter(
                            (option) =>
                                option.relation_type === "parent" &&
                                !flatValues.includes(option.id),
                        );
                        const childOption = rule.options.find(
                            (option) => option.relation_type === "child",
                        );
                        if (!childOption) return;
                        if (unselectedParentOptions?.length) {
                            // console.log(childOption.prompt_id, issue)

                            //until we can figure out how to get custom messages to work,
                            //we'll just use the label as a holder for all the info we need

                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: rule.label,
                                params: {
                                    prompt_id: childOption.prompt_id,
                                    child_option_id: childOption.id,
                                    type: "dependency",
                                },
                                path: [
                                    childOption.prompt_id,
                                    "dependency",
                                    "error",
                                    childOption.id,
                                ],
                                fatal: false,
                            });

                            // return z.NEVER;
                        } else {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: rule.label,
                                params: {
                                    prompt_id: childOption.prompt_id,
                                    child_option_id: childOption.id,
                                    type: "dependency",
                                },
                                path: [
                                    childOption.prompt_id,
                                    "dependency",
                                    "valid",
                                    childOption.id,
                                ],
                                fatal: false,
                            });
                        }
                    });

                //do numeric rules
                validationRules
                    .filter((rule) => rule.rule_type === "sum")
                    .forEach((rule) => {
                        const typedRule = rule as SumRule;
                        const { options } = typedRule;
                        //for now, we fetch all possible variable values
                        //includings base values, and accross all time horizons.
                        const variableValue = findVariableValue(typedRule);
                        if (!variableValue) return;

                        const initialValue = variableValue.numerical_value;

                        //TODO: this only works for choice types.
                        const sumOfSelectedValues = options
                            .filter((option) => {
                                return (
                                    flatValues.includes(option.id) ||
                                    flatValues.find(
                                        (optionLike) =>
                                            optionLike?.option_id ===
                                                option.id &&
                                            optionLike.is_selected,
                                    )
                                );
                            })
                            .reduce((carry, option) => {
                                return carry + Number(option.numerical_value);
                            }, 0);

                        let message: string;
                        const remainingAmount =
                            initialValue - sumOfSelectedValues;
                        const formattedValue = formatVariableValue(
                            variableValue.unit,
                            remainingAmount,
                            true,
                        );
                        if (typedRule.rule_position === "before") {
                            message = `${formattedValue} ${rule.label}`;
                        } else {
                            message = `${rule.label} ${formattedValue} `;
                        }

                        if (remainingAmount < 0) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.too_small,
                                message,
                                minimum: 0,
                                inclusive: true,
                                type: "number",
                                path: [rule.options[0].prompt_id, "sum"],
                            });
                        } else {
                            ctx.addIssue({
                                code: z.ZodIssueCode.too_big,
                                message,
                                maximum: initialValue,
                                inclusive: true,
                                type: "number",
                                path: [rule.options[0].prompt_id, "sum"],
                            });
                        }
                    });
            });

        //todo: figure out how to get custom error maps to work.
        //currently, the error map is being called, but transofmrations to errors don't show up
        //in the form.
        // z.setErrorMap((issue, ctx) => {
        //     if (issue.code === z.ZodIssueCode.custom) {
        //         switch (issue.params.type) {
        //             case "dependency":
        //                 //todo: figure out of there is some other way to pass extra data to the error message
        //                 return { ...issue, message: "updated" };
        //         }
        //     }
        //     console.log("issue is: down here");
        //     return { message: ctx.defaultError };
        // });

        return validation;
    }, []);

    const methods = useForm<FormObject>({
        defaultValues,
        resolver: zodResolver(validationSchema),

        mode: "all",
    });

    const {
        handleSubmit,
        // control
        trigger,
        formState,
    } = methods;

    //trigger all the fields that have validation rules on mount
    //to force validation messages and disabled states
    useEffect(() => {
        validationRules.forEach((rule) => {
            const option =
                rule.rule_type === "dependency"
                    ? rule.options.find(
                          (option) => option.relation_type === "child",
                      )
                    : rule.options[0];
            if (!option) return;
            trigger(option.prompt_id);
        });
    }, [defaultValues, validationRules]);

    // const watchedFields = useWatch({ control });

    useEffect(() => {
        if (defaultValues !== undefined) {
            // console.log("form defaultValues: ", defaultValues);
            // reset(defaultValues);
        }
    }, [defaultValues]);

    useEffect(() => {
        const { errors } = formState;
        const hasErrors =
            Object.keys(errors).filter((promptId) => {
                //if the error is a depdendency error, we can ignore it
                //because we know that the dependent option is not selected
                if (typeof errors[promptId] === "object") {
                    const error = errors[promptId] as any;

                    if (error.sum) {
                        //we use the ZodIssueCode.too_small to signify that the budget is less than 0
                        if (error.sum.type === z.ZodIssueCode.too_small)
                            return true;
                    }
                    if (error.dependency) {
                        return false;
                    }
                }
            })?.length > 0;
        if (hasErrors) {
            setInvalid();
        } else {
            setValid();
        }
    }, [formState]);

    if (!methods) return null;
    return (
        <FormProvider {...methods}>
            <form
                data-testid="designer-form-provider"
                data-submitting={formState.isSubmitting}
                onSubmit={handleSubmit(
                    () => {},
                    (errors) => {
                        //if we can confirm that all errors are due to missing dependencies
                        //and no dependent options are selected, we can submit the form
                        const errorsThatShouldPreventSubmissions = Object.keys(
                            errors,
                        ).filter((promptId) => {
                            //if the error is a depdendency error, we can ignore it
                            //because we know that the dependent option is not selected
                            if (typeof errors[promptId] === "object") {
                                const error = errors[promptId] as any;

                                if (error.sum) {
                                    //we use the ZodIssueCode.too_small to signify that the budget is less than 0
                                    if (
                                        error.sum.type ===
                                        z.ZodIssueCode.too_small
                                    )
                                        return true;
                                }
                                if (error.dependency) {
                                    return false;
                                }
                            }
                        });

                        if (!errorsThatShouldPreventSubmissions.length) {
                            return;
                        }

                        //otherwise, we need to show the errors
                        console.log("FORM INVALID");
                    },
                )}
                className="relative"
            >
                {children}
            </form>
        </FormProvider>
    );
};

export const FormContentBlockProvider = (props: Props) => {
    const {
        defaultValues,
        onSubmit,
        children,
        validationRules,
        contentBlockId,
        isImported,
    } = props;

    const formatVariableValue = useFormatVariableValue();

    const { setInvalid, setValid, isValid } = useIsSubmitting();
    type FormObject = typeof defaultValues;

    // Built-in validation message — only rendered for non-imported forms (see
    // the JSX gate below and the isImported prop docs).
    const [errorMessage, setErrorMessage] = useState("");

    const reducedRules = validationRules.reduce((carry, rule) => {
        const parentOptions = rule.options.filter(
            (option) => option.relation_type === "parent",
        );

        if (parentOptions.length) {
            return {
                ...carry,
                [parentOptions[0].prompt_id]: z.any(),
            };
        }
        return carry;
    }, {});

    const promptsForForm = usePromptsByForm(contentBlockId);
    //build first and THEN refine, so that the value passed to refine is the whole form state, one hopes
    const validationSchema = useMemo(() => {
        const validation = z
            .object({
                ...reducedRules,
                ...Object.keys(defaultValues).reduce((carry, key) => {
                    return { ...carry, [key]: z.any() };
                }, {}),
            })

            .partial()
            .superRefine((value, ctx) => {
                const flatValues = valuesToFlatArray(value);
                //do radio group rules
                //do dendency rules
                //do dendency rules
                validationRules
                    .filter((rule) => rule.rule_type === "dependency")
                    .forEach((rule) => {
                        const unselectedParentOptions = rule.options.filter(
                            (option) =>
                                option.relation_type === "parent" &&
                                !flatValues.includes(option.id),
                        );
                        const childOption = rule.options.find(
                            (option) => option.relation_type === "child",
                        );
                        if (!childOption) return;
                        if (unselectedParentOptions?.length) {
                            // console.log(childOption.prompt_id, issue)

                            //until we can figure out how to get custom messages to work,
                            //we'll just use the label as a holder for all the info we need

                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: rule.label,
                                params: {
                                    prompt_id: childOption.prompt_id,
                                    child_option_id: childOption.id,
                                    type: "dependency",
                                },
                                path: [
                                    childOption.prompt_id,
                                    "dependency",
                                    "error",
                                    childOption.id,
                                ],
                                fatal: false,
                            });

                            // return z.NEVER;
                        } else {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: rule.label,
                                params: {
                                    prompt_id: childOption.prompt_id,
                                    child_option_id: childOption.id,
                                    type: "dependency",
                                },
                                path: [
                                    childOption.prompt_id,
                                    "dependency",
                                    "valid",
                                    childOption.id,
                                ],
                                fatal: false,
                            });
                        }
                    });

                //do numeric rules
                validationRules
                    .filter((rule) => rule.rule_type === "equal")
                    .forEach((rule) => {
                        const typedRule = rule as SumRule;
                        const { options } = typedRule;
                        //for now, we fetch all possible variable values
                        //includings base values, and accross all time horizons.
                        const variableValue = findVariableValue(typedRule);
                        if (!variableValue) return;

                        const initialValue = variableValue.numerical_value;

                        //TODO: this only works for choice types.

                        let sumOfSelectedValues = 0;

                        sumOfSelectedValues = options
                            .filter((option) => {
                                return (
                                    flatValues.includes(option.id) ||
                                    flatValues.find(
                                        (optionLike) =>
                                            optionLike?.option_id ===
                                                option.id &&
                                            optionLike.is_selected,
                                    ) ||
                                    numericalInputTypes.includes(
                                        option.prompt_type,
                                    )
                                );
                            })
                            .reduce((carry, option) => {
                                if (
                                    !numericalInputTypes.includes(
                                        option.prompt_type,
                                    )
                                ) {
                                    return (
                                        carry + Number(option.numerical_value)
                                    );
                                }
                                if (
                                    !isNaN(Number(getValues(option.prompt_id)))
                                ) {
                                    return (
                                        carry +
                                        Number(getValues(option.prompt_id))
                                    );
                                }
                                return carry;
                            }, 0);

                        let message: string;
                        const remainingAmount =
                            initialValue - sumOfSelectedValues;
                        const formattedValue = formatVariableValue(
                            variableValue.unit,
                            remainingAmount,
                            true,
                        );
                        if (typedRule.rule_position === "before") {
                            message = `${formattedValue} ${rule.label}`;
                        } else {
                            message = `${rule.label} ${formattedValue} `;
                        }

                        // Validity is projected from formState (see effect
                        // below) — only register the issue here, don't push the
                        // global flag from inside the refine.
                        if (remainingAmount !== 0) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.too_small,
                                message,
                                minimum: 0,
                                inclusive: true,
                                type: "number",
                                path: [rule.options[0].prompt_id, "sum"],
                            });
                        }
                        setErrorMessage(message);
                    });

                validationRules
                    .filter((rule) => rule.rule_type === "difference")
                    .forEach((rule) => {
                        const typedRule = rule as SumRule;
                        const { options } = typedRule;
                        //for now, we fetch all possible variable values
                        //includings base values, and accross all time horizons.
                        const variableValue = findVariableValue(typedRule);
                        if (!variableValue) return;

                        const initialValue = variableValue.numerical_value;

                        //TODO: this only works for choice types.

                        let sumOfSelectedValues = 0;

                        sumOfSelectedValues = options
                            .filter((option) => {
                                return (
                                    flatValues.includes(option.id) ||
                                    flatValues.find(
                                        (optionLike) =>
                                            optionLike?.option_id ===
                                                option.id &&
                                            optionLike.is_selected,
                                    ) ||
                                    numericalInputTypes.includes(
                                        option.prompt_type,
                                    )
                                );
                            })
                            .reduce((carry, option) => {
                                if (
                                    !numericalInputTypes.includes(
                                        option.prompt_type,
                                    )
                                ) {
                                    return (
                                        carry + Number(option.numerical_value)
                                    );
                                }
                                if (
                                    !isNaN(Number(getValues(option.prompt_id)))
                                ) {
                                    return (
                                        carry +
                                        Number(getValues(option.prompt_id))
                                    );
                                }
                                return carry;
                            }, 0);

                        let message: string;
                        const remainingAmount =
                            initialValue - sumOfSelectedValues;
                        const formattedValue = formatVariableValue(
                            variableValue.unit,
                            remainingAmount,
                            true,
                        );
                        if (typedRule.rule_position === "before") {
                            message = `${formattedValue} ${rule.label}`;
                        } else {
                            message = `${rule.label} ${formattedValue} `;
                        }

                        if (remainingAmount < 0) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.too_small,
                                message,
                                minimum: 0,
                                inclusive: true,
                                type: "number",
                                path: [rule.options[0].prompt_id, "sum"],
                            });
                        }
                        setErrorMessage(message);
                    });

                validationRules
                    .filter((rule) => rule.rule_type === "sum")
                    .forEach((rule) => {
                        const typedRule = rule as SumRule;
                        const { options } = typedRule;
                        //for now, we fetch all possible variable values
                        //includings base values, and accross all time horizons.
                        const variableValue = findVariableValue(typedRule);
                        if (!variableValue) return;

                        const initialValue = variableValue.numerical_value;

                        //TODO: this only works for choice types.

                        let sumOfSelectedValues = 0;

                        sumOfSelectedValues = options
                            .filter((option) => {
                                return (
                                    flatValues.includes(option.id) ||
                                    flatValues.find(
                                        (optionLike) =>
                                            optionLike?.option_id ===
                                                option.id &&
                                            optionLike.is_selected,
                                    ) ||
                                    numericalInputTypes.includes(
                                        option.prompt_type,
                                    )
                                );
                            })
                            .reduce((carry, option) => {
                                if (
                                    !numericalInputTypes.includes(
                                        option.prompt_type,
                                    )
                                ) {
                                    return (
                                        carry - Number(option.numerical_value)
                                    );
                                }
                                if (
                                    !isNaN(Number(getValues(option.prompt_id)))
                                ) {
                                    return (
                                        carry -
                                        Number(getValues(option.prompt_id))
                                    );
                                }
                                return carry;
                            }, 0);

                        let message: string;
                        const remainingAmount =
                            initialValue - sumOfSelectedValues;
                        const formattedValue = formatVariableValue(
                            variableValue.unit,
                            remainingAmount,
                            true,
                        );
                        if (typedRule.rule_position === "before") {
                            message = `${formattedValue} ${rule.label}`;
                        } else {
                            message = `${rule.label} ${formattedValue} `;
                        }

                        if (remainingAmount < Math.max(typedRule.min, 0)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.too_small,
                                message,
                                minimum: 0,
                                inclusive: true,
                                type: "number",
                                path: [
                                    rule.options[rule.options.length - 1]
                                        .prompt_id,
                                    "sum",
                                ],
                            });
                        }
                        setErrorMessage(message);
                    });

                promptsForForm.forEach((prompt) => {
                    // A slider at 0 is answered; an empty Multiple Select is
                    // the answer "none of these" — see isPromptUnanswered.
                    if (isPromptUnanswered(prompt, value[prompt.id])) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "This field is required",
                            path: [prompt.id],
                        });
                    }
                });
            });

        return validation;
    }, [promptsForForm, defaultValues, validationRules]);

    const methods = useForm<FormObject>({
        defaultValues,
        resolver: zodResolver(validationSchema),
        mode: "all",
    });

    const {
        handleSubmit,
        reset,
        getValues,
        control,
        trigger,
        formState,
    } = methods;

    // Force a FULL-form re-validation whenever any value changes. RHF, on a
    // single field change, re-runs the resolver but only refreshes the changed
    // field's error key — so a budget error parked on options[0] froze at its
    // initial value while other sliders moved. Watching all values + trigger()
    // re-runs the whole schema so every rule's error stays current.
    const watchedValues = useWatch({ control });
    // Key on the serialized values so this only fires on a real value change —
    // not on every re-render (trigger() updates formState, which re-renders;
    // depending on the object reference would loop).
    const watchedValuesKey = JSON.stringify(watchedValues);
    useEffect(() => {
        void trigger();
    }, [watchedValuesKey, trigger]);

    //trigger all the fields that have validation rules on mount
    //to force validation messages and disabled states
    useEffect(() => {
        validationRules.forEach((rule) => {
            const option =
                rule.rule_type === "dependency"
                    ? rule.options.find(
                          (option) => option.relation_type === "child",
                      )
                    : rule.options[0];
            if (!option) return;
            trigger(option.prompt_id);
        });
    }, [defaultValues, validationRules]);

    // const watchedFields = useWatch({ control });

    useEffect(() => {
        if (defaultValues !== undefined) {
            // console.log("form defaultValues: ", defaultValues);
            reset(defaultValues);
            // reset() seeds the saved values but CLEARS all validation errors and
            // does not re-validate. Without re-running validation here, a
            // previously-submitted (and possibly invalid) allocation loads with an
            // empty error set — so it reads as valid and the submit button stays
            // enabled until the participant happens to touch a slider. Re-validate
            // the seeded values so the loaded state is gated immediately.
            void trigger();
        }
    }, [defaultValues, reset, trigger]);

    // Project React Hook Form's real validation result onto the global
    // submit-disabled flag. Reading formState.errors here subscribes the
    // component to error changes (mode: "all" re-validates on every change), so
    // the disabled state of the out-of-form submit button always tracks the
    // SAME errors that render the red message. This replaces the old approach
    // of pushing setInvalid()/setValid() from inside the zod refine, which
    // let the two drift apart.
    const hasBlockingErrors =
        getBlockingErrorPromptIds(formState.errors).length > 0;
    useEffect(() => {
        if (hasBlockingErrors) {
            setInvalid();
        } else {
            setValid();
        }
    }, [hasBlockingErrors]);

    // Portal target for the built-in message on slider forms (the last slider's
    // message slot). Only used when !isImported; the query is harmless otherwise.
    const sliderErrorMessageEls = useGetAllElementsAsync(
        `form[data-form-id="${contentBlockId}"] .question-wrapper div[data-message-location]`,
    );
    const sliderErrorMessageEl =
        sliderErrorMessageEls?.[sliderErrorMessageEls?.length - 1];

    if (!methods) return null;

    return (
        <FormProvider {...methods}>
            <form
                data-form-id={contentBlockId}
                data-testid="participant-form-provider"
                data-submitting={formState.isSubmitting}
                onSubmit={handleSubmit(
                    () => {
                        // console.log("handleSubmit", getValues());
                        //why do we have to call getValues() here?
                        if ("function" === typeof onSubmit)
                            return onSubmit(getValues());
                    },
                    (errors) => {
                        // Same blocking-error rule the disabled state uses, so a
                        // form that looks blocked (red, button disabled) is never
                        // quietly persisted here, and vice versa.
                        const errorsThatShouldPreventSubmissions =
                            getBlockingErrorPromptIds(errors);

                        if (!errorsThatShouldPreventSubmissions.length) {
                            onSubmit(getValues());
                            return;
                        }

                        //otherwise, we need to show the errors
                        console.log("FORM INVALID");
                    },
                )}
                className="relative"
            >
                {children}

                {/* Built-in validation message. Imported designs render their
                    own (correctly-formatted) display, so suppress this to avoid
                    a duplicate; non-imported forms still need it. */}
                {!isImported && !!errorMessage && !sliderErrorMessageEl && (
                    <div
                        data-is-error={!isValid}
                        className="data-[is-error=true]:text-red-500 data-[is-error=false]:text-green-600"
                    >
                        {errorMessage}
                    </div>
                )}

                {!isImported &&
                    !!errorMessage &&
                    !!sliderErrorMessageEl &&
                    createPortal(
                        <div
                            data-is-error={!isValid}
                            className="data-[is-error=true]:text-red-500 data-[is-error=false]:text-green-600"
                        >
                            {errorMessage}
                        </div>,
                        sliderErrorMessageEl,
                    )}
            </form>
        </FormProvider>
    );
};
